import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Users, CalendarDays, Sparkles, BookOpen, CreditCard, LogOut, Sun, TrendingUp, FileText, Receipt, Send, Newspaper, MessageSquare, Lightbulb, DollarSign } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import FeedbackLink from "@/components/FeedbackLink";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true, tid: "nav-overview" },
  { to: "/dashboard/students", label: "Students", icon: Users, tid: "nav-students" },
  { to: "/dashboard/schedule", label: "Schedule", icon: CalendarDays, tid: "nav-schedule" },
  { to: "/dashboard/ai-picks", label: "Resource Picks", icon: Sparkles, tid: "nav-ai-picks" },
  { to: "/dashboard/resources", label: "Resources", icon: BookOpen, tid: "nav-resources" },
  { to: "/dashboard/worksheets", label: "Worksheets", icon: FileText, tid: "nav-worksheets" },
  { to: "/dashboard/receipts", label: "Receipts", icon: Receipt, tid: "nav-receipts" },
  { to: "/dashboard/referrals", label: "Referrals", icon: Send, tid: "nav-referrals" },
  { to: "/dashboard/billing", label: "Billing", icon: CreditCard, tid: "nav-billing" },
];

const ADMIN_NAV = [
  { to: "/dashboard/admin/revenue", label: "Revenue", icon: DollarSign, tid: "nav-admin-revenue" },
  { to: "/dashboard/growth", label: "Growth", icon: TrendingUp, tid: "nav-growth" },
  { to: "/dashboard/admin/blog", label: "Blog", icon: Newspaper, tid: "nav-admin-blog" },
  { to: "/dashboard/admin/feedback", label: "Feedback", icon: MessageSquare, tid: "nav-admin-feedback" },
  { to: "/dashboard/admin/suggestions", label: "Suggestions", icon: Lightbulb, tid: "nav-admin-suggestions" },
];

// Hidden allowlist — the Admin sidebar section only renders for these exact emails,
// even if a user somehow ends up with role="admin" in the DB. Defense-in-depth.
const ADMIN_ALLOWED_EMAILS = new Set([
  "rmh06c@yahoo.com",
  "admin@fhome.edu", // seeded test account for QA
]);

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    nav("/");
  };

  return (
    <div className="min-h-screen bg-[#F5F5F4] flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-white border-r border-stone-200 h-screen sticky top-0 flex flex-col p-4" data-testid="dashboard-sidebar">
        <Link to="/" className="flex items-center gap-2 font-display font-bold text-lg px-2 py-3" data-testid="sidebar-brand">
          <span className="inline-grid place-items-center w-8 h-8 rounded-xl bg-amber-500 text-white">
            <Sun className="w-4 h-4" />
          </span>
          HomeSchool Hub
        </Link>

        <nav className="mt-6 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-amber-50 text-amber-700"
                    : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
          {user?.role === "admin" && ADMIN_ALLOWED_EMAILS.has((user?.email || "").toLowerCase()) && (
            <>
              <div className="mt-4 mb-1 px-3 text-[10px] uppercase tracking-[0.18em] text-stone-400 font-semibold">Admin</div>
              {ADMIN_NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={n.tid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-amber-50 text-amber-700"
                        : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" />
                  {n.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="mt-auto pt-4 border-t border-stone-200">
          <div className="px-3 py-2 text-sm">
            <div className="font-medium text-stone-900 truncate" data-testid="sidebar-username">{user?.name}</div>
            <div className="text-stone-500 truncate" data-testid="sidebar-useremail">{user?.email}</div>
          </div>
          <div className="px-3 py-1 text-xs">
            <FeedbackLink variant="muted" label="Send feedback →" className="text-stone-500 hover:text-stone-800" />
          </div>
          <Button
            variant="ghost"
            onClick={doLogout}
            className="w-full justify-start rounded-xl text-stone-600 hover:text-stone-900"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="max-w-6xl mx-auto p-6 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
